import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric8,
  type DashboardMetric8CycleFraudRiskCount,
} from '@/services/dashboardService';

const { Text } = Typography;

const CYCLE_COLORS = [
  '#1F77B4',
'#FF7F0E',
'#2CA02C',
'#D62728',
'#17BECF',
'#9467BD',
'#BCBD22',
'#E377C2',
'#8C564B',
'#40E0D0',
'#FF9896',
'#228B22',
'#FFD166',
'#4B0082',
'#06D6A0',
'#B22222',
'#4682B4',
'#F3722C',
'#6A5ACD',
'#20B2AA',
'#C51B7D',
'#9ACD32',
'#F8961E',
'#277DA1',
'#8E4585',
'#43AA8B',
];

type PieDatum = {
  cycleId: string;
  cycleLabel: string; // "A00 Revenue"
  fraudRiskCount: number;
  percent: number; // 0..100
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const Metric8Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d: PieDatum | undefined = payload?.[0]?.payload;
  if (!d) return null;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 240,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{d.cycleLabel}</div>

      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>Fraud risks</span>
        <span style={{ fontWeight: 700 }}>{d.fraudRiskCount}</span>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 4,
        }}
      >
        <span>Percent</span>
        <span style={{ fontWeight: 700 }}>{formatPercent(d.percent)}</span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click slice to open Exhibit 4 (Fraud Risk)
      </div>
    </div>
  );
};

// Label inside slice: "12 (36.4%)"
const renderSliceLabel = (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, value, payload } = props;
  const percent = payload?.percent ?? 0;

  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const RADIAN = Math.PI / 180;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 4) return null;

  return (
    <text
      x={x}
      y={y}
      fill='#111827'
      textAnchor='middle'
      dominantBaseline='central'
      style={{ fontSize: 12, fontWeight: 600 }}
    >
      {`${value} (${formatPercent(percent)})`}
    </text>
  );
};

export type EX1SpecificIdentifiedFraudRisksByCyclePieChartProps = {
  height?: number;
};

export const EX1SpecificIdentifiedFraudRisksByCyclePieChart: React.FC<
  EX1SpecificIdentifiedFraudRisksByCyclePieChartProps
> = ({ height = 360 }) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric8CycleFraudRiskCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Same responsive legend fix you used for Metric #2 / #7
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric8(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 8 (Fraud risk count breakdown by cycle).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const totalFraudRisks = useMemo(() => {
    return raw.reduce((sum, x) => sum + (x.fraudRiskCount || 0), 0);
  }, [raw]);

  const data: PieDatum[] = useMemo(() => {
    const total = totalFraudRisks || 0;
    return raw
      .filter(x => (x.fraudRiskCount ?? 0) > 0)
      .map(x => ({
        cycleId: x.cycleId,
        cycleLabel: `${x.cycleCode} ${x.cycleName}`.trim(),
        fraudRiskCount: x.fraudRiskCount,
        percent: total > 0 ? (x.fraudRiskCount / total) * 100 : 0,
      }))
      //.sort((a, b) => b.fraudRiskCount - a.fraudRiskCount);
  }, [raw, totalFraudRisks]);

  const isEmpty = !loading && !errorMessage && data.length === 0;

  const title = `Specific Identified Fraud Risks by Cycle (Total Count: ${totalFraudRisks})`;
  // const title = `8. Specific Identified Fraud Risks by Cycle`;

  // Exhibit 4, fraud tab filter (adjust query params if your EX4 contract differs)
  const goToExhibit4FraudForCycle = (cycleId: string) => {
    navigate('/risk-assessment', {
      state: {
        cycleId: encodeURIComponent(cycleId),
        fraud: true,
      },
    });
  };

  return (
    <Card title={title}>
      <div style={{ height }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 8 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No fraud risks were returned for this report.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <PieChart
              margin={
                isNarrow
                  ? { top: 8, right: 8, bottom: 24, left: 8 }
                  : { top: 8, right: 140, bottom: 8, left: 8 }
              }
            >
              <Tooltip content={<Metric8Tooltip />} />

              <Legend
                layout={isNarrow ? 'horizontal' : 'vertical'}
                align={isNarrow ? 'center' : 'right'}
                verticalAlign={isNarrow ? 'bottom' : 'middle'}
                wrapperStyle={
                  isNarrow ? { paddingTop: 12 } : { paddingLeft: 16 }
                }
              />

              <Pie
                data={data}
                dataKey='fraudRiskCount'
                nameKey='cycleLabel'
                cx={isNarrow ? '50%' : '40%'}
                cy='50%'
                outerRadius={120}
                paddingAngle={1}
                isAnimationActive={false}
                label={renderSliceLabel}
                labelLine={false}
                onClick={(_, idx) => {
                  const d = data[idx];
                  if (d?.cycleId) goToExhibit4FraudForCycle(d.cycleId);
                }}
                style={{ cursor: 'pointer' }}
              >
                {data.map((_, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={CYCLE_COLORS[idx % CYCLE_COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {!loading && !errorMessage ? (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Text type='secondary'>
            Specific fraud risk factors per cycle, % of total
          </Text>
        </div>
      ) : null}
    </Card>
  );
};

export default EX1SpecificIdentifiedFraudRisksByCyclePieChart;
